import { Link, useParams, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  MapPin,
  Star,
  Users,
  Loader2,
  AlertCircle,
  ArrowLeft,
  Bell,
  ExternalLink,
  Image as ImageIcon,
  RefreshCw,
  Tent,
  DollarSign,
  TriangleAlert,
} from 'lucide-react';
import { MainLayout } from '../layouts/MainLayout';
import { AvailabilityCalendar } from '../components/AvailabilityCalendar';
import { AvailabilitySnapshot } from '../components/AvailabilitySnapshot';
import { FirstComeFirstServedCard } from '../components/FirstComeFirstServedCard';
import { FacilityAvailabilityGrid } from '../components/FacilityAvailabilityGrid';
import { RequestForm } from '../components/RequestForm';
import { SearchContextBar } from '../components/SearchContextBar';
import { WeatherCard } from '../components/WeatherCard';
import { computeEndDate, formatIsoDateShort } from '../utils/dates';
import { useFacility, useAvailability, useFacilityAvailability, useRefreshFacility } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { JsonLd } from '../components/JsonLd';
import { ExpandableText } from '../components/ExpandableText';
import { trackViewFacility } from '../lib/posthog';
import type { Campsite } from '../types';

function addUtcDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function bookedDotClass(pct: number): string {
  if (pct >= 0.97) return 'bg-red-500';
  if (pct >= 0.8) return 'bg-amber-500';
  return 'bg-emerald-500';
}

const SHORT_MONTH_DAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

const SHORT_MONTH_DAY_YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

const DAY_ONLY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  timeZone: 'UTC',
});

function formatGridRangeLabel(startIso: string, endIso: string): string {
  const start = new Date(`${startIso}T00:00:00.000Z`);
  const end = new Date(`${endIso}T00:00:00.000Z`);
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();
  const startLabel = sameYear
    ? SHORT_MONTH_DAY_FORMATTER.format(start)
    : SHORT_MONTH_DAY_YEAR_FORMATTER.format(start);
  const endLabel = sameMonth
    ? DAY_ONLY_FORMATTER.format(end)
    : sameYear
      ? SHORT_MONTH_DAY_FORMATTER.format(end)
      : SHORT_MONTH_DAY_YEAR_FORMATTER.format(end);
  return `${startLabel}-${endLabel}`;
}

function siteNightLabel(count: number): string {
  return count === 1 ? 'site-night' : 'site-nights';
}

const STALENESS_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes
const CLOSED_NOTICE_RE = /closed for the .* season|closed until/i;
const MAX_AMENITY_CHIPS = 8;

export function FacilityPage() {
  const { id } = useParams({ from: '/facility/$id' });
  const searchParams = useSearch({ from: '/facility/$id' });
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const searchEndDate = useMemo(() => {
    if (!searchParams.startDate || !searchParams.nights) return undefined;
    return computeEndDate(searchParams.startDate, searchParams.nights);
  }, [searchParams.startDate, searchParams.nights]);

  const [selectedCampsite, setSelectedCampsite] = useState<Campsite | null>(null);
  const [selectedDates, setSelectedDates] = useState<{ start: string; end: string } | null>(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [mainImageFailed, setMainImageFailed] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showAllNotices, setShowAllNotices] = useState(false);
  const [gridRange, setGridRange] = useState(() => {
    const start = searchParams.startDate
      ? new Date(`${searchParams.startDate}T00:00:00.000Z`)
      : new Date();
    start.setUTCHours(0, 0, 0, 0);
    const end = addUtcDays(start, 13);
    return { startDate: start.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10) };
  });

  const { data: facility, isLoading: isLoadingFacility, error: facilityError } = useFacility(id);
  const facilityAvailability = useFacilityAvailability(id, gridRange.startDate, gridRange.endDate);

  const refreshFacility = useRefreshFacility();
  const hasAttemptedRefresh = useRef(false);
  const alertCtaRef = useRef<HTMLDivElement | null>(null);

  const lastScrapedAt = facilityAvailability.data?.lastScrapedAt;
  const isStale = !lastScrapedAt || (Date.now() - new Date(lastScrapedAt).getTime() > STALENESS_THRESHOLD_MS);
  const isRefreshing = refreshFacility.isPending;

  // Auto-refresh on first load if stale.
  // Gate on isSuccess so we don't fire before the initial GET completes.
  // refreshFacility.mutate is a stable reference in TanStack Query v5.
  const refreshMutate = refreshFacility.mutate;
  useEffect(() => {
    if (facilityAvailability.isSuccess && isStale && id && !hasAttemptedRefresh.current) {
      hasAttemptedRefresh.current = true;
      refreshMutate(id);
    }
  }, [facilityAvailability.isSuccess, isStale, id, refreshMutate]);

  usePageMeta({
    title: facility ? `${facility.name} - Campsite Availability | HellaCamping` : undefined,
    description: facility?.description?.trim() || (facility ? `Check campsite availability at ${facility.name} on HellaCamping.` : undefined),
    canonicalPath: `/facility/${id}`,
    ogImage: facility?.images?.[0] ?? undefined,
  });

  useEffect(() => {
    if (facility) {
      trackViewFacility(id, facility.name);
    }
  }, [id, facility]);

  const startDate = selectedDates?.start || new Date().toISOString().split('T')[0];
  const endDate = selectedDates?.end || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const { data: availability } = useAvailability(
    selectedCampsite?.id || facility?.campsites?.[0]?.id || '',
    startDate,
    endDate
  );

  const images = facility?.images ?? [];

  const campgroundLd = useMemo(() => {
    if (!facility) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Campground',
      name: facility.name,
      description: facility.description || undefined,
      ...(facility.latitude && facility.longitude
        ? { geo: { '@type': 'GeoCoordinates', latitude: facility.latitude, longitude: facility.longitude } }
        : {}),
      ...(images.length > 0 ? { image: images } : {}),
      ...(facility.amenities && facility.amenities.length > 0
        ? { amenityFeature: facility.amenities.map((a: string) => ({ '@type': 'LocationFeatureSpecification', name: a })) }
        : {}),
      url: `https://hellacamping.com/facility/${id}`,
      numberOfRooms: facility.campsites?.length ?? 0,
    };
  }, [facility, id, images]);

  const breadcrumbLd = useMemo(() => {
    if (!facility) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hellacamping.com/' },
        ...(facility.location
          ? [{ '@type': 'ListItem', position: 2, name: facility.location, item: `https://hellacamping.com/search` }]
          : []),
        { '@type': 'ListItem', position: facility.location ? 3 : 2, name: facility.name },
      ],
    };
  }, [facility]);

  if (isLoadingFacility) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-600 mb-4" />
          <p className="text-gray-600">Loading facility details...</p>
        </div>
      </MainLayout>
    );
  }

  if (facilityError || !facility) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-16">
          <div className="p-4 bg-red-50 rounded-full mb-4">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Facility not found</h3>
          <p className="text-gray-600 mb-4">{facilityError?.message || 'Unable to load facility details'}</p>
          <button
            onClick={() => navigate({
              to: '/search',
              search: {
                startDate: searchParams.startDate,
                nights: searchParams.nights,
                guests: searchParams.guests,
              },
            })}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Search
          </button>
        </div>
      </MainLayout>
    );
  }

  const placeholderImage = '/placeholder-campground.svg';
  const heroImage = images[selectedImageIndex] ?? images[0] ?? placeholderImage;
  const resolvedHeroImage = mainImageFailed ? placeholderImage : heroImage;

  const overviewStats = facility.stats ?? null;
  const visibleRangeStats = facilityAvailability.data?.stats ?? null;
  const visibleRangeStatsUnavailableReason =
    facilityAvailability.data?.statsUnavailableReason ?? null;
  const gridRangeLabel = formatGridRangeLabel(gridRange.startDate, gridRange.endDate);
  const overviewStatsScopeLabel = overviewStats ? `next ${overviewStats.windowDays} days` : null;
  const season = facility.season ?? null;
  const isSeasonClosed = season?.status === 'closed';
  const reopensLabel = formatIsoDateShort(season?.reopensOn);
  const campsiteCount = facility.campsites?.length ?? 0;
  const notices = facility.notices ?? [];
  const closedNotice = notices.find((notice) => CLOSED_NOTICE_RE.test(notice)) ?? null;
  const visibleNotices = showAllNotices ? notices : notices.slice(0, 3);
  const amenities = facility.amenities ?? [];

  const handleAlertClick = () => {
    if (!isAuthenticated) {
      navigate({ to: '/auth' });
      return;
    }
    setShowRequestForm(true);
    alertCtaRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <MainLayout>
      {campgroundLd && <JsonLd data={campgroundLd} />}
      {breadcrumbLd && <JsonLd data={breadcrumbLd} />}
      <div className="max-w-6xl mx-auto space-y-6">
        <SearchContextBar
          startDate={searchParams.startDate}
          nights={searchParams.nights}
          guests={searchParams.guests}
          {...(searchParams.fromParkId
            ? {
                backLabel: `Back to ${facility.location || 'Park'}`,
                backTo: '/park/$id',
                backRouteParams: { id: searchParams.fromParkId },
              }
            : {})}
        />

        {/* Hero */}
        <div>
          <div className="h-72 sm:h-96 rounded-xl overflow-hidden relative bg-gray-100">
            <img
              src={resolvedHeroImage}
              alt={facility.name}
              className="object-cover w-full h-full"
              onError={() => setMainImageFailed(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
              {searchParams.fromParkId && (
                <Link
                  to="/park/$id"
                  params={{ id: searchParams.fromParkId }}
                  search={{
                    startDate: searchParams.startDate,
                    nights: searchParams.nights,
                    guests: searchParams.guests,
                  }}
                  className="inline-flex items-center gap-1.5 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-gray-800 hover:bg-white mb-2"
                >
                  <MapPin className="w-3 h-3" />
                  {facility.location || 'Back to park'}
                </Link>
              )}
              <h1 className="text-3xl sm:text-5xl font-bold text-white tracking-tight">
                {facility.name}
              </h1>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-gray-800">
                  {campsiteCount.toLocaleString()} campsite{campsiteCount === 1 ? '' : 's'}
                </span>
                {facility.rating > 0 && (
                  <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-gray-800 tabular-nums">
                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    {facility.rating.toFixed(1)}
                  </span>
                )}
                {isSeasonClosed ? (
                  <span className="inline-flex items-center gap-1.5 bg-red-600/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-white">
                    Closed for the season{reopensLabel ? ` · opens ${reopensLabel}` : ''}
                  </span>
                ) : overviewStats && overviewStatsScopeLabel ? (
                  <span className="inline-flex items-center gap-1.5 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-gray-800 tabular-nums">
                    <span className={`w-2 h-2 rounded-full ${bookedDotClass(overviewStats.bookedPct)}`} />
                    {overviewStats.openSiteNights.toLocaleString()} open {siteNightLabel(overviewStats.openSiteNights)} · {overviewStatsScopeLabel}
                  </span>
                ) : null}
                {!isSeasonClosed && overviewStats?.dataAgeHours != null && (
                  <span className="bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-gray-800 tabular-nums">
                    {overviewStats.dataAgeHours === 0 ? 'just checked' : `checked ${overviewStats.dataAgeHours}h ago`}
                  </span>
                )}
              </div>
            </div>
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-2">
              {images.slice(0, 5).map((img, index) => (
                <button
                  key={img}
                  type="button"
                  onClick={() => { setSelectedImageIndex(index); setMainImageFailed(false); }}
                  aria-label={`Show photo ${index + 1}`}
                  className={`h-14 w-20 rounded-md overflow-hidden border-2 transition-colors ${
                    index === selectedImageIndex ? 'border-emerald-600' : 'border-transparent hover:border-gray-300'
                  }`}
                >
                  <img
                    src={img}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = placeholderImage;
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Closure banner */}
        {closedNotice && !isSeasonClosed && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-2">
              <TriangleAlert className="w-4 h-4 text-red-700" />
              <h2 className="text-sm font-semibold text-red-900">
                This campground is currently closed
              </h2>
            </div>
            <p className="mt-2 text-sm text-red-800 line-clamp-2">{closedNotice}</p>
          </div>
        )}

        {/* Notices */}
        {notices.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-2">
              <TriangleAlert className="w-4 h-4 text-amber-700" />
              <h2 className="text-sm font-semibold text-amber-900">
                Notices ({notices.length})
              </h2>
            </div>
            <ul className="mt-2 space-y-1.5">
              {visibleNotices.map((notice, index) => (
                <li key={index} className="text-sm text-amber-800 line-clamp-2">
                  {notice}
                </li>
              ))}
            </ul>
            {notices.length > 3 && (
              <button
                type="button"
                onClick={() => setShowAllNotices((v) => !v)}
                className="mt-2 text-sm font-medium text-amber-900 hover:text-amber-700"
              >
                {showAllNotices ? 'Show fewer' : `+${notices.length - 3} more`}
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="lg:grid lg:grid-cols-3 lg:gap-6 space-y-6 lg:space-y-0">
          <div className="lg:col-span-2 space-y-6">
            {/* About */}
            {facility.description?.trim() ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h2 className="text-lg font-bold text-gray-900 mb-3">About {facility.name}</h2>
                <ExpandableText
                  text={facility.description}
                  className="text-sm leading-relaxed text-gray-700 whitespace-pre-line"
                />
              </div>
            ) : null}

            {/* Availability grid */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Availability grid</h2>
                <button
                  type="button"
                  onClick={() => refreshFacility.mutate(id)}
                  disabled={isRefreshing}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  title="Refresh availability data"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
              {isSeasonClosed ? (
                <div className="bg-red-50 rounded-lg p-3 text-sm mb-4">
                  <p className="text-red-900 font-medium">
                    Closed for the season{reopensLabel ? ` — opens ${reopensLabel}` : ''}.
                  </p>
                  <p className="text-red-800/80 mt-0.5">
                    This campground isn't taking reservations for these dates.
                  </p>
                </div>
              ) : visibleRangeStats && visibleRangeStats.openSiteNights === 0 ? (
                <div className="bg-emerald-50 rounded-lg p-3 text-sm mb-4 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-emerald-900">
                    No openings in the visible dates ({gridRangeLabel}) — set an alert and we'll watch it for you.
                  </p>
                  <button
                    type="button"
                    onClick={handleAlertClick}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-xs font-medium"
                  >
                    <Bell className="w-3.5 h-3.5" />
                    Set alert
                  </button>
                </div>
              ) : null}
              {!isSeasonClosed && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 mb-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold tracking-wider uppercase text-gray-500">
                        Visible dates · {gridRangeLabel}
                      </p>
                      {visibleRangeStats ? (
                        <p className="mt-1 text-sm text-gray-700 tabular-nums">
                          <span className="font-semibold text-gray-900">
                            {visibleRangeStats.openSiteNights.toLocaleString()} open {siteNightLabel(visibleRangeStats.openSiteNights)}
                          </span>
                          {' '}of {visibleRangeStats.totalSiteNights.toLocaleString()} · {Math.round(visibleRangeStats.bookedPct * 100)}% booked
                        </p>
                      ) : facilityAvailability.isFetching ? (
                        <p className="mt-1 text-sm text-gray-600">Checking visible-date summary…</p>
                      ) : visibleRangeStatsUnavailableReason === 'not_tracked' ? (
                        <p className="mt-1 text-sm text-gray-600">
                          These dates are outside currently tracked availability. Choose dates within the tracked window or check back closer to your trip.
                        </p>
                      ) : (
                        <p className="mt-1 text-sm text-gray-600">
                          Summary unavailable for these dates. Try refreshing to check for newer availability data.
                        </p>
                      )}
                    </div>
                    {visibleRangeStats?.dataAgeHours != null && (
                      <p className="text-xs text-gray-500 tabular-nums">
                        {visibleRangeStats.dataAgeHours === 0 ? 'Just checked' : `Checked ${visibleRangeStats.dataAgeHours}h ago`}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {facility.campsites && facility.campsites.length > 0 ? (
                <div className="relative">
                  {isRefreshing && (
                    <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
                      <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm">
                        <RefreshCw className="w-4 h-4 animate-spin text-emerald-600" />
                        <span className="text-sm text-gray-600">Updating availability...</span>
                      </div>
                    </div>
                  )}
                  <FacilityAvailabilityGrid
                    campsites={facility.campsites}
                    availability={facilityAvailability.data?.availability || []}
                    startDate={gridRange.startDate}
                    endDate={gridRange.endDate}
                    highlightStartDate={searchParams.startDate}
                    highlightEndDate={searchEndDate}
                    selectedCampsiteId={selectedCampsite?.id ?? null}
                    onSelectCampsite={(campsite) => setSelectedCampsite(campsite)}
                    onRangeChange={(range) => setGridRange(range)}
                    firstComeFirstServed={facility.firstComeFirstServed}
                  />
                </div>
              ) : (
                <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                  <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No campsites found for this facility</p>
                </div>
              )}
            </div>

            {/* Selected campsite */}
            {selectedCampsite && (
              <div className="space-y-6">
                <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                  {selectedCampsite.images && selectedCampsite.images.length > 0 && (
                    <div className="h-40 overflow-hidden bg-gray-100">
                      <img
                        src={selectedCampsite.images[0]}
                        alt={selectedCampsite.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="p-4 space-y-3">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">{selectedCampsite.name}</h2>
                      {selectedCampsite.type && (
                        <p className="text-sm text-gray-500 capitalize">{selectedCampsite.type.replace(/_/g, ' ')}</p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-3 text-sm text-gray-700">
                      {selectedCampsite.capacity > 0 && (
                        <span className="inline-flex items-center gap-1.5">
                          <Users className="w-4 h-4 text-gray-400" />
                          Up to {selectedCampsite.capacity} guests
                        </span>
                      )}
                      {selectedCampsite.pricePerNight > 0 && (
                        <span className="inline-flex items-center gap-1.5">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          ${selectedCampsite.pricePerNight}/night
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5">
                        <Tent className="w-4 h-4 text-gray-400" />
                        {selectedCampsite.type ? selectedCampsite.type.replace(/_/g, ' ') : 'Site'}
                      </span>
                    </div>

                    {selectedCampsite.amenities && selectedCampsite.amenities.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {selectedCampsite.amenities.map((amenity) => (
                          <span
                            key={amenity}
                            className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs"
                          >
                            {amenity}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Availability Calendar</h2>
                  <AvailabilityCalendar
                    availability={availability || []}
                    onDateSelect={setSelectedDates}
                    selectedDates={selectedDates}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Right rail */}
          <div className="lg:col-span-1 lg:sticky lg:top-6 self-start space-y-6">
            {/* Availability snapshot */}
            {overviewStats && !isSeasonClosed ? (
              <AvailabilitySnapshot stats={overviewStats} onAlertClick={handleAlertClick} />
            ) : facility.firstComeFirstServed ? (
              <FirstComeFirstServedCard />
            ) : null}

            {/* Weather */}
            {facility.latitude && facility.longitude ? (
              <WeatherCard lat={facility.latitude} lon={facility.longitude} />
            ) : null}

            {/* Facts */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h2 className="text-sm font-bold text-gray-900 mb-3">Campground facts</h2>
              <div className="space-y-2 text-sm text-gray-600">
                <p className="flex items-center gap-2">
                  <Tent className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  {campsiteCount.toLocaleString()} campsite{campsiteCount === 1 ? '' : 's'}
                </p>
                {facility.location && (
                  searchParams.fromParkId ? (
                    <Link
                      to="/park/$id"
                      params={{ id: searchParams.fromParkId }}
                      search={{
                        startDate: searchParams.startDate,
                        nights: searchParams.nights,
                        guests: searchParams.guests,
                      }}
                      className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700"
                    >
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      Part of {facility.location}
                    </Link>
                  ) : (
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      Part of {facility.location}
                    </p>
                  )
                )}
                {facility.sourceUrl && (
                  <a
                    href={facility.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-emerald-600 hover:text-emerald-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View on {facility.sourceName}
                  </a>
                )}
              </div>
              {amenities.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Amenities</p>
                  <div className="flex flex-wrap gap-1.5">
                    {amenities.slice(0, MAX_AMENITY_CHIPS).map((amenity) => (
                      <span
                        key={amenity}
                        className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs"
                      >
                        {amenity}
                      </span>
                    ))}
                    {amenities.length > MAX_AMENITY_CHIPS && (
                      <span className="px-2.5 py-1 bg-gray-100 text-gray-500 rounded-full text-xs">
                        +{amenities.length - MAX_AMENITY_CHIPS} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div ref={alertCtaRef}>
          {isAuthenticated ? (
            showRequestForm ? (
              <RequestForm
                facilityId={facility.id}
                campsiteId={selectedCampsite?.id}
                initialStartDate={searchParams.startDate}
                initialEndDate={searchEndDate}
                onSuccess={() => setShowRequestForm(false)}
                onCancel={() => setShowRequestForm(false)}
              />
            ) : (
              <div className="bg-emerald-50 rounded-xl p-6 text-center">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">Can't find availability?</h3>
                <p className="text-gray-600 mb-4">
                  Create an alert and we'll notify you when a spot opens up.
                </p>
                <button
                  onClick={() => setShowRequestForm(true)}
                  className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
                >
                  Create Availability Alert
                </button>
              </div>
            )
          ) : (
            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Want to get notified?</h3>
              <p className="text-gray-600 mb-4">
                Sign in to create availability alerts for this campground.
              </p>
              <button
                onClick={() => navigate({ to: '/auth' })}
                className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
              >
                Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
